import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button
} from '@mui/material';
import { Helmet } from 'react-helmet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PublicIcon from '@mui/icons-material/Public';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ModuleAnalyticsDashboard from '../components/ModuleAnalyticsDashboard';

const UserActivity = ({
  initialSearch = '',
  selectedUser = null,
  onClearUser = null,
  onBackToUsers = null
}) => {
  const [selectedContentType, setSelectedContentType] = useState('all');

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Helmet>
        <title>User Activity & Analytics | GPN Admin</title>
      </Helmet>

      {/* Top Header & Back Button if embedded */}
      <Box sx={{ mb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            {onBackToUsers && (
              <Button
                variant="outlined"
                size="small"
                onClick={onBackToUsers}
                startIcon={<ArrowBackIcon />}
                sx={{
                  color: '#64d2e2',
                  borderColor: 'rgba(100, 210, 226, 0.4)',
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#64d2e2',
                    bgcolor: 'rgba(100, 210, 226, 0.08)'
                  }
                }}
              >
                Back to Users
              </Button>
            )}
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                background: 'linear-gradient(135deg, #64d2e2 0%, #ffffff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.5px'
              }}
            >
              {selectedUser ? `${selectedUser.name}'s Activity` : 'User Activity & Engagement Analytics'}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 0.5 }}>
            {selectedUser
              ? `Tracking verified engagement and interaction timeline specifically for ${selectedUser.name} (${selectedUser.email || `ID #${selectedUser.id}`})`
              : 'Consolidated user interaction timeline, engagement analytics, trend charts, and real-time activity verification'}
          </Typography>
        </Box>

        {/* Ministry Content Switcher */}
        <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, p: 0.5, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Tabs
            value={selectedContentType}
            onChange={(e, newVal) => setSelectedContentType(newVal)}
            sx={{
              minHeight: 36,
              '& .MuiTabs-indicator': { backgroundColor: '#177373', height: '100%', borderRadius: 1.5, zIndex: 0 },
              '& .MuiTab-root': {
                minHeight: 36,
                py: 0.5,
                px: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.825rem',
                color: 'rgba(255, 255, 255, 0.7)',
                zIndex: 1,
                borderRadius: 1.5,
                transition: 'all 0.2s ease',
                '&.Mui-selected': { color: '#FFFFFF' }
              }
            }}
          >
            <Tab value="all" icon={<PublicIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="All Ministries" />
            <Tab value="prayer" icon={<VolunteerActivismIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Prayers" />
            <Tab value="special_prayer" icon={<MeetingRoomIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Prayer Closet" />
            <Tab value="devotion" icon={<MenuBookIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Daily Devotions" />
          </Tabs>
        </Box>
      </Box>

      {/* Main Consolidated Analytics Dashboard */}
      <ModuleAnalyticsDashboard
        key={`${selectedContentType}_${selectedUser?.id || 'all'}`}
        contentType={selectedContentType}
        userId={selectedUser?.id}
        selectedUser={selectedUser}
        initialSearch={initialSearch}
        onClearUser={onClearUser}
        moduleTitle=""
        hideHeader={true}
      />
    </Box>
  );
};

export default UserActivity;
